import { hasExactly, isPlainRecord, marketRuleSetting, resolveMarketRule } from './market-rule-kit.js';
export const BOARDING_PIN_KEY = 'safety.boardingPin';
/** The moments the PIN may be chosen for, in ride order: a driver is assigned and the rider has not boarded. */
export const BOARDING_PIN_PHASES = ['assigned', 'en_route', 'arrived', 'waiting', 'pin_pending'];
/** The moments it is always shown: the driver is at the pickup and asks for it. */
export const BOARDING_PIN_REQUIRED_PHASES = ['arrived', 'waiting', 'pin_pending'];
/**
 * D722 (D716 §5 Q5): the members of `safety.boardingPin` the owner explicitly let a state or a market decide AGAINST the layers above it (the protected-key rule's
 * documented exception). Every other member stays protected: a lower scope only tightens it.
 */
export const BOARDING_PIN_OVERRIDABLE_MEMBERS = ['enabled'];
const inOrder = (phases) => BOARDING_PIN_PHASES.filter((p) => phases.includes(p));
/** How each member is tightened while it is PROTECTED: the PIN stays on if a layer above has it on; it is shown only in the moments every layer shows it. */
const PROTECTED_TIGHTEN = {
    enabled: (d, e) => d.enabled || e.enabled,
    visiblePhases: (d, e) => inOrder(d.visiblePhases.filter((p) => e.visiblePhases.includes(p))),
};
/**
 * D722: fold a deciding (more specific) version under an enclosing one. A member in `overridable` (by default {@link BOARDING_PIN_OVERRIDABLE_MEMBERS}) is taken
 * from the deciding version as is; every other member is tightened ({@link PROTECTED_TIGHTEN}). The list is what decides - drop `enabled` from it and a market
 * can no longer turn the PIN off under a country that has it on.
 */
export function tightenBoardingPin(deciding, enclosing, overridable = BOARDING_PIN_OVERRIDABLE_MEMBERS) {
    const member = (k) => (overridable.includes(k) ? deciding[k] : PROTECTED_TIGHTEN[k](deciding, enclosing));
    return { enabled: member('enabled'), visiblePhases: member('visiblePhases') };
}
export const DEFAULT_BOARDING_PIN = { enabled: true, visiblePhases: [...BOARDING_PIN_PHASES] };
export const STRICT_BOARDING_PIN = { enabled: true, visiblePhases: [...BOARDING_PIN_REQUIRED_PHASES] };
export const BOARDING_PIN_REASON_CODES = ['BOARDING_PIN_GOVERNED', 'BOARDING_PIN_DEFAULT', 'BOARDING_PIN_INVALID'];
export function boardingPinIssue(v) {
    if (!isPlainRecord(v))
        return 'NOT_AN_OBJECT';
    // D722: `enabled` is optional on the way in (a version written before it carries none and reads `true`); every version written since carries it.
    if (!hasExactly(v, ['enabled', 'visiblePhases']) && !hasExactly(v, ['visiblePhases']))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    if ('enabled' in v && typeof v.enabled !== 'boolean')
        return 'INVALID_ENABLED';
    const phases = v.visiblePhases;
    if (!Array.isArray(phases) || phases.some((p) => !BOARDING_PIN_PHASES.includes(p)) || new Set(phases).size !== phases.length)
        return 'INVALID_VISIBLE_PHASES';
    if (BOARDING_PIN_REQUIRED_PHASES.some((p) => !phases.includes(p)))
        return 'REQUIRED_PHASE_MISSING';
    return undefined;
}
export const BOARDING_PIN_SPEC = {
    key: BOARDING_PIN_KEY,
    issue: boardingPinIssue,
    copy: (v) => ({ enabled: v.enabled ?? true, visiblePhases: inOrder(v.visiblePhases) }),
    defaultValue: DEFAULT_BOARDING_PIN,
    strictValue: STRICT_BOARDING_PIN,
    reasonPrefix: 'BOARDING_PIN',
    constraintClass: 'safety',
    // Never shown in more moments than a layer above shows it; `enabled` is the deciding version's own (D722: the owner's explicit override) - see tightenBoardingPin.
    tighten: (deciding, enclosing) => tightenBoardingPin(deciding, enclosing),
};
export function boardingPinSetting(input) {
    return marketRuleSetting(BOARDING_PIN_SPEC, input);
}
export function resolveBoardingPin(settings, marketId, at, regionCode) {
    return resolveMarketRule(BOARDING_PIN_SPEC, settings, marketId, at, regionCode);
}
export function boardingPinWire(decision) {
    return { enabled: decision.value.enabled, visiblePhases: inOrder(decision.value.visiblePhases), policyVersion: decision.policyVersion };
}
/** D722: why a ride has (or has no) boarding PIN - the stable reason a ride snapshots with the deciding version. */
export const BOARDING_PIN_REQUIREMENT_CODES = ['BOARDING_PIN_REQUIRED', 'BOARDING_PIN_DISABLED'];
/**
 * D722: whether a ride starting under `decision` needs the boarding PIN. A decision that could not be read at all (`undefined`: the store failed) is the strict
 * reading - the PIN is required (fail safe). Only a version that says `enabled: false` waives it.
 */
export function boardingPinRequirement(decision) {
    if (decision === undefined)
        return { required: true, reasonCode: 'BOARDING_PIN_REQUIRED', policyVersion: `${BOARDING_PIN_KEY}@unavailable` };
    return decision.value.enabled
        ? { required: true, reasonCode: 'BOARDING_PIN_REQUIRED', policyVersion: decision.policyVersion }
        : { required: false, reasonCode: 'BOARDING_PIN_DISABLED', policyVersion: decision.policyVersion };
}
//# sourceMappingURL=boarding-pin-policy.js.map